import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { VehicleRentalService } from '../../../../services/vehicle-rental.service';
import { VehiclesService } from '../../../../services/vehicles.service';
import { Vehicle } from '../../../../models/Vehicle';
import { CustomersService } from '../../../../services/customers.service';
import { Customer } from '../../../../models/Customer';
import { ViaCepService, ViaCepResponse } from '../../../../services/viacep.service';
import { environment } from '../../../../../environments/environment';
import { StaffService } from '../../../../services/staff.service';
import { StaffMember } from '../../../../models/StaffMember';
import { StatusService } from '../../../../services/status.service';
import { Status } from '../../../../models/Status';

// Interfaces locais para evitar problemas de importação
interface VehicleRental {
  id?: number;
  rentalNumber: string;
  startDate: string;
  endDate: string;
  priority: string;
  customerId?: number;
  driverName?: string;
}

@Component({
  selector: 'app-vehicle-rental-form',
  templateUrl: './vehicle-rental-form.component.html',
  styleUrls: ['./vehicle-rental-form.component.css']
})
export class VehicleRentalFormComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  // Formulários
  rentalForm!: FormGroup;
  currentStep = 1;
  totalSteps = 4;
  isEditMode = false;
  isSaving = false;
  isLoading = false;
  
  // Anexos (apenas no modo de edição)
  attachments: any[] = [];
  attachmentCategories: any[] = [];
  selectedFiles: File[] = [];
  isUploading = false;

  // CEP do motorista
  isLoadingDriverCep = false;
  driverCepError: string | null = null;

  // Status
  loadingStatuses = false;
  statusOptions: Status[] = [];

  // Endereço do motorista vem do cliente selecionado
  useCustomerAddress = true;


  // Lista de clientes vindos do endpoint
  customers: Customer[] = [];
  isLoadingCustomers = false;

  // Lista de veículos vindos do endpoint
  vehicles: Vehicle[] = [];
  isLoadingVehicles = false;

  // Lista de funcionários
  staff: StaffMember[] = [];
  isLoadingStaff = false;

  priorityOptions = [
    { value: 'BAIXA', label: 'Baixa' },
    { value: 'NORMAL', label: 'Normal' },
    { value: 'ALTA', label: 'Alta' },
    { value: 'URGENTE', label: 'Urgente' }
  ];

  // Anexos serão gerenciados apenas na edição

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private vehicleRentalService: VehicleRentalService,
    private vehiclesService: VehiclesService,
    private customersService: CustomersService,
    private viaCep: ViaCepService,
    private staffService: StaffService,
    private statusService: StatusService
  ) {
    this.initializeForm();
  }

  ngOnInit(): void {
    // Carregar clientes, veículos e funcionários existentes para os selects
    this.loadCustomers();
    this.loadVehicles();
    this.loadStaff();
    this.loadRentalStatuses();

    this.route.params.pipe(takeUntil(this.destroy$)).subscribe(params => {
      if (params['id']) {
        this.isEditMode = true;
        this.totalSteps = 5; // Adiciona step de anexos no modo de edição
        this.loadRentalData(Number(params['id']));
        this.loadAttachmentCategories();
        this.loadExistingAttachments(Number(params['id']));
      } else {
        this.generateRentalNumber();
        this.addVehicle();
      }
    });

    // Anexos serão carregados apenas na edição

    // Calcular dias automaticamente quando as datas mudarem
    this.rentalForm.get('startDate')?.valueChanges.subscribe(() => this.calculateRentalDays());
    this.rentalForm.get('endDate')?.valueChanges.subscribe(() => this.calculateRentalDays());
  }

  private loadCustomers(): void {
    this.isLoadingCustomers = true;
    this.customersService.getActive().subscribe({
      next: (list) => {
        this.customers = list || [];
        this.isLoadingCustomers = false;
      },
      error: (err) => {
        console.error('Erro ao carregar clientes:', err);
        this.isLoadingCustomers = false;
      }
    });
  }

  private loadVehicles(): void {
    this.isLoadingVehicles = true;
    this.vehiclesService.getAll().subscribe({
      next: (list) => {
        this.vehicles = list || [];
        this.isLoadingVehicles = false;
      },
      error: (err) => {
        console.error('Erro ao carregar veículos:', err);
        this.isLoadingVehicles = false;
      }
    });
  }

  calculateRentalDays(): void {
    const startDate = this.rentalForm.get('startDate')?.value;
    const endDate = this.rentalForm.get('endDate')?.value;

    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const diffTime = Math.abs(end.getTime() - start.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 para incluir o dia final

      this.rentalForm.patchValue({ rentalDays: diffDays });
      this.calculateTotals();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeForm(): void {
    this.rentalForm = this.fb.group({
      // Step 1: Dados Básicos
      rentalNumber: ['', [Validators.required, Validators.maxLength(50)]],
      priority: ['NORMAL', Validators.required],
      assignedStaffId: ['', Validators.required],
      startDate: ['', Validators.required],
      endDate: ['', Validators.required],
      notes: ['', Validators.maxLength(500)],

    // Step 2: Cliente e Motorista
      customerId: ['', Validators.required],
      driverName: ['', Validators.maxLength(200)],
      driverDocument: ['', Validators.maxLength(18)],
      driverZipCode: ['', Validators.maxLength(9)],
      driverStreet: ['', Validators.maxLength(200)],
      driverNumber: ['', Validators.maxLength(20)],
      driverComplement: ['', Validators.maxLength(100)],
      driverDistrict: ['', Validators.maxLength(100)],
      driverCity: ['', Validators.maxLength(100)],
      driverState: ['', Validators.maxLength(2)],

    // Step 3: Veículos
      vehicles: this.fb.array([]),

      status: ['Pendente', Validators.required],

      // Step 4: Condições
      pickupLocation: ['', Validators.maxLength(200)],
      returnLocation: ['', Validators.maxLength(200)],
      insuranceIncluded: [false]
    });
  }

  private loadStaff(): void {
    this.isLoadingStaff = true;
    // Usar a mesma abordagem da tela de listagem: endpoint paginado
    this.staffService.getStaffPaginated({ page: 1, pageSize: 100 }).subscribe({
      next: (response) => {
        // A resposta vem como objeto com propriedade 'staff'
        this.staff = (response?.staff ?? []);
        this.isLoadingStaff = false;
      },
      error: (err) => {
        console.error('Erro ao carregar funcionários (paginated):', err);
        // Fallback: tentar endpoint simples caso esteja disponível
        this.staffService.getStaff().subscribe({
          next: (list) => {
            this.staff = list || [];
            this.isLoadingStaff = false;
          },
          error: (err2) => {
            console.error('Erro ao carregar funcionários (fallback getStaff):', err2);
            this.staff = [];
            this.isLoadingStaff = false;
          }
        });
      }
    });
  }

  private loadRentalStatuses(): void {
    this.loadingStatuses = true;
    this.statusService.getByEntity('VehicleRental', true)
      .subscribe({
        next: (statuses: Status[]) => {
          // Filtrar apenas status com entityType = 'VehicleRental' para garantir
          this.statusOptions = statuses.filter(
            status => status.entityType === 'VehicleRental' && status.isActive !== false
          );
          this.loadingStatuses = false;
        },
        error: (err) => {
          console.error('Erro ao carregar status de locações:', err);
          this.loadingStatuses = false;
          // Fallback para status básicos em caso de erro
          this.statusOptions = [
            { id: 1, name: 'Pendente', description: 'Locação pendente', entityType: 'VehicleRental', isActive: true },
            { id: 2, name: 'Confirmada', description: 'Locação confirmada', entityType: 'VehicleRental', isActive: true },
            { id: 3, name: 'Em Andamento', description: 'Locação em andamento', entityType: 'VehicleRental', isActive: true },
            { id: 4, name: 'Concluída', description: 'Locação concluída', entityType: 'VehicleRental', isActive: true },
            { id: 5, name: 'Cancelada', description: 'Locação cancelada', entityType: 'VehicleRental', isActive: true }
          ] as Status[];
        }
      });
  }

  get vehiclesFormArray(): FormArray {
    return this.rentalForm.get('vehicles') as FormArray;
  }

  private generateRentalNumber(): void {
    const timestamp = Date.now();
    const random = Math.floor(Math.random() * 1000);
    const number = `LOC-${timestamp}-${random}`;
    this.rentalForm.patchValue({ rentalNumber: number });
  }

  addVehicle(): void {
    const vehicleForm = this.fb.group({
      vehicleId: ['', Validators.required],
      startMileage: [0, [Validators.required, Validators.min(0)]],
      conditionAtStart: ['', Validators.maxLength(500)],
      notes: ['', Validators.maxLength(200)],
      // Removed financial fields from item form
    });

    this.vehiclesFormArray.push(vehicleForm);
    this.calculateTotals();
  }

  removeVehicle(index: number): void {
    if (this.vehiclesFormArray.length > 1) {
      this.vehiclesFormArray.removeAt(index);
      this.calculateTotals();
    }
  }

  calculateTotals(): void {
    // Financial totals removed per new contract; no-op
  }

  nextStep(): void {
    if (this.currentStep < this.totalSteps) {
      // Se estiver no step de anexos (step 4 no modo de edição), fazer upload automático
      if (this.currentStep === 4 && this.isEditMode && this.selectedFiles.length > 0) {
        this.uploadAttachmentsAutomatically();
      } else {
      this.currentStep++;
      }
    }
  }

  previousStep(): void {
    if (this.currentStep > 1) {
      this.currentStep--;
    }
  }

  canGoNext(): boolean {
    switch (this.currentStep) {
      case 1:
        const step1Controls = ['rentalNumber', 'priority', 'assignedStaffId', 'startDate', 'endDate'];
        return step1Controls.every(field => this.rentalForm.get(field)?.valid);
      case 2:
        // Step 2: Cliente - validar apenas se customerId está preenchido
        const customerId = this.rentalForm.get('customerId')?.value;
        return customerId && customerId !== '' && customerId !== null && customerId !== undefined;
      case 3:
        // Step 3: Veículos - permitir avançar mesmo sem veículos
        return this.vehiclesFormArray.length === 0 || this.vehiclesFormArray.controls.every(control => control.valid);
      case 4:
        // Step de anexos (apenas no modo de edição) - sempre pode avançar
        return true;
      default:
        return true;
    }
  }

  // ====== CEP: helpers e integração ViaCEP ======
  onDriverZipCodeInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const masked = this.maskCEP(input.value || '');
    this.rentalForm.get('driverZipCode')?.setValue(masked, { emitEvent: false });

    const digits = this.onlyDigits(masked);
    if (digits.length === 8) {
      this.fetchDriverAddressByCEP(digits);
    } else {
      this.driverCepError = null;
    }
  }

  private onlyDigits(value: string | undefined): string {
    return (value || '').replace(/\D+/g, '');
  }

  private maskCEP(value: string): string {
    const digits = this.onlyDigits(value).slice(0, 8);
    return digits.replace(/(\d{5})(\d)/, '$1-$2');
  }

  private fetchDriverAddressByCEP(cepDigits: string): void {
    if (!cepDigits || cepDigits.length !== 8) return;
    this.isLoadingDriverCep = true;
    this.driverCepError = null;
    this.viaCep.lookup(cepDigits).subscribe({
      next: (res: ViaCepResponse) => {
        this.isLoadingDriverCep = false;
        if ((res as any)?.erro) {
          this.driverCepError = 'CEP não encontrado.';
          return;
        }
        this.rentalForm.patchValue({
          driverStreet: res.logradouro || this.rentalForm.get('driverStreet')?.value,
          driverDistrict: res.bairro || this.rentalForm.get('driverDistrict')?.value,
          driverCity: res.localidade || this.rentalForm.get('driverCity')?.value,
          driverState: res.uf || this.rentalForm.get('driverState')?.value,
          driverComplement: res.complemento || this.rentalForm.get('driverComplement')?.value
        }, { emitEvent: false });
      },
      error: _ => {
        this.isLoadingDriverCep = false;
        this.driverCepError = 'Erro ao consultar o CEP.';
      }
    });
  }

  async onSubmit(): Promise<void> {
    if (this.rentalForm.valid) {
      this.isSaving = true;

      try {
        const rentalData = this.mapFormToDTO();

        if (this.isEditMode) {
          // Atualizar locação existente
          const rentalId = this.route.snapshot.params['id'];
          await this.vehicleRentalService.updateVehicleRental(Number(rentalId), rentalData).toPromise();
          
          // Salvar anexos se houver
          if (this.attachments.length > 0) {
            await this.saveAttachmentsToRental(Number(rentalId));
          }
        } else {
          // Criar nova locação
          const createdRental = await this.vehicleRentalService.createVehicleRental(rentalData).toPromise();
          const rentalId = createdRental?.id;

          if (!rentalId) {
            throw new Error('Erro ao criar locação: ID não retornado');
          }
        }

        this.isSaving = false;
        this.router.navigate(['/commercial/vehicle-rentals']);
      } catch (error) {
        console.error('Erro ao salvar locação:', error);
        this.isSaving = false;
        alert('Erro ao salvar locação. Tente novamente.');
      }
    } else {
      this.markFormGroupTouched(this.rentalForm);
    }
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      if (control) {
        control.markAsTouched();
        if (control instanceof FormGroup) {
          this.markFormGroupTouched(control);
        } else if (control instanceof FormArray) {
          control.controls.forEach(ctrl => {
            if (ctrl instanceof FormGroup) {
              this.markFormGroupTouched(ctrl);
            }
          });
        }
      }
    });
  }

  cancel(): void {
    this.router.navigate(['/commercial/vehicle-rentals']);
  }


  calculateEstimatedTotal(): number {
    const vehicles = this.vehiclesFormArray.length;
    const days = this.calculateDaysBetweenDates(
      this.rentalForm.get('startDate')?.value,
      this.rentalForm.get('endDate')?.value
    );
    return vehicles * days * 50; // R$ 50 por dia por veículo (valor mock)
  }

  private calculateDaysBetweenDates(startDate: string, endDate: string): number {
    if (!startDate || !endDate) return 0;

    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  }

  getTodayDate(): string {
    const today = new Date();
    return today.toISOString().split('T')[0];
  }

  formatDate(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  }

  // ===== CARREGAMENTO DE DADOS =====

  loadRentalData(rentalId: number): void {
    this.isLoading = true;
    
    this.vehicleRentalService.getVehicleRentalById(rentalId).subscribe({
      next: (rental) => {
        this.populateForm(rental);
        this.loadRentalItems(rentalId);
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Erro ao carregar dados da locação:', error);
        this.isLoading = false;
        alert('Erro ao carregar dados da locação. Tente novamente.');
        this.router.navigate(['/commercial/vehicle-rentals']);
      }
    });
  }

  populateForm(rental: any): void {
    // Preencher dados básicos
    this.rentalForm.patchValue({
      rentalNumber: rental.rentalNumber,
      priority: rental.priority,
      startDate: this.formatDateForInput(rental.startDate),
      endDate: this.formatDateForInput(rental.endDate),
      notes: rental.notes,
      customerId: rental.customerId,
      assignedStaffId: rental.assignedStaffId,
      driverName: rental.driverName,
      driverDocument: rental.driverDocument,
      pickupLocation: rental.pickupLocation,
      returnLocation: rental.returnLocation,
      insuranceIncluded: rental.insuranceIncluded,
      status: rental.status
    });

    // Atualizar dados do cliente se disponível
    if (rental.customerName) {
      const existingCustomer = this.customers.find(c => c.id === rental.customerId);
      if (!existingCustomer) {
        // Adicionar cliente mínimo compatível com a interface Customer
        this.customers.push({
          id: rental.customerId,
          name: rental.customerName || 'N/A',
          tradeName: rental.customerName || undefined,
          documentNumber: rental.customerDocument || '',
          email: rental.customerEmail || '',
          primaryPhone: rental.customerPhone || '',
          active: true,
          blocked: false
        });
      }
    }
  }

  loadRentalItems(rentalId: number): void {
    this.vehicleRentalService.getVehicleRentalItemsByRental(rentalId).subscribe({
      next: (items) => {
        this.populateVehiclesFormArray(items);
      },
      error: (error) => {
        console.error('Erro ao carregar itens da locação:', error);
        // Continuar mesmo sem itens
      }
    });
  }

  populateVehiclesFormArray(items: any[]): void {
    // Limpar array existente
    while (this.vehiclesFormArray.length !== 0) {
      this.vehiclesFormArray.removeAt(0);
    }

    // Adicionar itens
    items.forEach(item => {
      // Verificar se o veículo existe na lista, se não, adicionar
      const existingVehicle = this.vehicles.find(v => v.id === item.vehicleId);
      if (!existingVehicle && item.vehicleId) {
        this.vehicles.push({
          id: item.vehicleId,
          plateNumber: item.plateNumber || '',
          chassisNumber: item.chassisNumber || '',
          brand: item.brand || '',
          model: item.model || '',
          modelYear: item.modelYear || new Date().getFullYear(),
          manufactureYear: item.manufactureYear || item.modelYear || new Date().getFullYear(),
          ownershipType: item.ownershipType || '',
          status: item.status || 'Ativo'
        });
      }

      const vehicleForm = this.fb.group({
        vehicleId: [item.vehicleId, Validators.required],
        startMileage: [item.startMileage, [Validators.required, Validators.min(0)]],
        conditionAtStart: [item.conditionAtStart, Validators.maxLength(500)],
        notes: [item.notes, Validators.maxLength(200)],
        // Removed financial fields from populated item form
      });

      this.vehiclesFormArray.push(vehicleForm);
    });

    // Recalcular totais
    this.calculateTotals();
  }

  formatDateForInput(dateString: string | Date): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0]; // Formato YYYY-MM-DD
  }

  // ===== MAPEAMENTO DE DADOS =====

  mapFormToDTO(): any {
    const formValue = this.rentalForm.value;
    
    // Mapear dados básicos da locação
    const rentalDTO = {
      // Dados básicos
      rentalNumber: formValue.rentalNumber,
      startDate: formValue.startDate,
      endDate: formValue.endDate,
      status: formValue.status,
      priority: formValue.priority,
      notes: formValue.notes,
      assignedStaffId: parseInt(formValue.assignedStaffId) || 0,
      
      // Cliente - dados separados conforme DTO
      customerId: parseInt(formValue.customerId) || 0,
      customerName: (this.getSelectedCustomer()?.tradeName || this.getSelectedCustomer()?.name) || '',
      customerDocument: this.getSelectedCustomer()?.documentNumber || '',
      customerEmail: this.getSelectedCustomer()?.email || '',
      customerPhone: this.getSelectedCustomer()?.primaryPhone || '',
      
      // Motorista
      driverName: formValue.driverName,
      driverDocument: formValue.driverDocument,
      
      // Financial fields removed from DTO
      
      // Localização
      pickupLocation: formValue.pickupLocation,
      returnLocation: formValue.returnLocation,
      
      // Seguro
      insuranceIncluded: formValue.insuranceIncluded,
      
      // Campos adicionais do DTO
      paymentStatus: 'Pendente',
      
      // Itens da locação (veículos)
      rentalItems: this.mapVehiclesToRentalItems()
    };

    return rentalDTO;
  }

  getSelectedStaff(): StaffMember | undefined {
    const staffId = this.rentalForm.get('assignedStaffId')?.value;
    const numericId = typeof staffId === 'string' ? parseInt(staffId) : staffId;
    return this.staff.find(s => s.Id === numericId);
  }

  mapVehiclesToRentalItems(): any[] {
    
    return this.vehiclesFormArray.controls.map((vehicleControl, index) => {
      const vehicleValue = vehicleControl.value;
      
      // Tentar diferentes formas de busca
      let selectedVehicle = this.vehicles.find(v => v.id === vehicleValue.vehicleId);
      if (!selectedVehicle) {
        selectedVehicle = this.vehicles.find(v => v.id === parseInt(vehicleValue.vehicleId));
      }
      if (!selectedVehicle) {
        selectedVehicle = this.vehicles.find(v => v.id === Number(vehicleValue.vehicleId));
      }
      
      
      const fallbackVehicle: Vehicle = {
        id: 0,
        plateNumber: '',
        chassisNumber: '',
        brand: '',
        model: '',
        modelYear: new Date().getFullYear(),
        manufactureYear: new Date().getFullYear(),
        ownershipType: '',
        status: '',
        renavamNumber: '' // Adicione isto
      };
      const sv: Vehicle = selectedVehicle || fallbackVehicle;
      
      return {
        // Campos obrigatórios para validação
        vehicleRentalId: 0, // Será preenchido pelo backend
        vehicleId: parseInt(vehicleValue.vehicleId) || 0,
        plateNumber: sv.plateNumber || 'N/A',
        brand: sv.brand || 'N/A',
        model: sv.model || 'N/A',
        modelYear: sv.modelYear || new Date().getFullYear(),
        
        // Financial fields removed from item DTO
        
        // Campos opcionais
        vehicleCategory: 'PASSEIO',
        startMileage: parseInt(vehicleValue.startMileage) || 0,
        endMileage: null,
        conditionAtStart: vehicleValue.conditionAtStart || '',
        conditionAtEnd: null,
        hasDamage: false,
        missingAccessories: false,
        damageDescription: null,
        notes: vehicleValue.notes || '',
        status: 'ATIVO'
      };
    });
  }

  getSelectedCustomer(): Customer | undefined {
    const customerId = this.rentalForm.get('customerId')?.value;
    
    // Converter para number se necessário
    const numericCustomerId = typeof customerId === 'string' ? parseInt(customerId) : customerId;
    const customer = this.customers.find(c => c.id === numericCustomerId);
    
    return customer;
  }

  // ===== MÉTODOS DE ANEXOS (APENAS NO MODO DE EDIÇÃO) =====

  loadAttachmentCategories(): void {
    // Mock de categorias por enquanto
    this.attachmentCategories = [
      { id: 1, name: 'DOCUMENTO', description: 'Documentos gerais' },
      { id: 2, name: 'CONTRATO', description: 'Contratos e termos' },
      { id: 3, name: 'FOTO', description: 'Fotos e imagens' },
      { id: 4, name: 'OUTRO', description: 'Outros arquivos' }
    ];
  }

  onFileSelected(event: any): void {
    const files = event.target.files;
    if (files && files.length > 0) {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (this.isValidFileType(file)) {
          this.selectedFiles.push(file);
        } else {
          alert(`Tipo de arquivo não permitido: ${file.name}`);
        }
      }
    }
  }

  removeFile(index: number): void {
    this.selectedFiles.splice(index, 1);
  }

  isValidFileType(file: File): boolean {
    const allowedTypes = [
      'application/pdf',
      'image/jpeg',
      'image/png',
      'image/gif',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];
    return allowedTypes.includes(file.type);
  }

  getFileSizeDisplay(size: number): string {
    if (size < 1024) return size + ' B';
    if (size < 1024 * 1024) return (size / 1024).toFixed(1) + ' KB';
    return (size / (1024 * 1024)).toFixed(1) + ' MB';
  }

  getFileIconClass(file: File): string {
    const type = file.type;
    if (type.includes('pdf')) return 'fas fa-file-pdf text-red-500';
    if (type.includes('image')) return 'fas fa-file-image text-green-500';
    if (type.includes('word')) return 'fas fa-file-word text-blue-500';
    if (type.includes('excel') || type.includes('sheet')) return 'fas fa-file-excel text-green-600';
    return 'fas fa-file text-gray-500';
  }

  uploadAttachmentsAutomatically(): void {
    if (this.selectedFiles.length === 0) {
      this.currentStep++;
      return;
    }

    this.isUploading = true;

    // Upload dos arquivos para blob storage
    let completedUploads = 0;
    const totalFiles = this.selectedFiles.length;

    this.selectedFiles.forEach((file, index) => {
      this.vehicleRentalService.uploadFileToBlob(file).subscribe({
        next: (response) => {
          // Armazenar anexo para salvar no update
          const attachment = {
            fileName: file.name,
            filePath: response.url,
            fileSize: file.size,
            mimeType: file.type,
            category: 'DOCUMENTO',
            description: `Anexo ${index + 1}`,
            status: 'ATIVO',
            isRequired: false,
            isPublic: false
          };

          this.attachments.push(attachment);
          completedUploads++;
          
          if (completedUploads === totalFiles) {
            this.isUploading = false;
            this.selectedFiles = [];
            this.currentStep++; // Avançar para o próximo step
          }
        },
        error: (error) => {
          console.error('Erro no upload:', error);
          completedUploads++;
          
          if (completedUploads === totalFiles) {
            this.isUploading = false;
            this.currentStep++; // Avançar mesmo com erro
          }
        }
      });
    });
  }

  uploadAttachments(): void {
    if (this.selectedFiles.length === 0) {
      alert('Selecione pelo menos um arquivo para upload');
      return;
    }

    this.isUploading = true;
    const rentalId = this.route.snapshot.params['id']; // Pegar ID da rota

    if (!rentalId) {
      alert('ID da locação não encontrado');
      this.isUploading = false;
      return;
    }

    // Upload dos arquivos
    this.selectedFiles.forEach((file, index) => {
      this.vehicleRentalService.uploadFileToBlob(file).subscribe({
        next: (response) => {
          // Criar registro de anexo
          const attachmentData = {
            vehicleRentalId: parseInt(rentalId),
            fileName: file.name,
            filePath: response.url,
            fileSize: file.size,
            mimeType: file.type,
            category: 'DOCUMENTO',
            description: `Anexo ${index + 1}`,
            status: 'ATIVO',
            isRequired: false,
            isPublic: false
          };

          this.vehicleRentalService.createVehicleRentalAttachment(attachmentData).subscribe({
            next: (attachment) => {
              this.attachments.push(attachment);
              if (index === this.selectedFiles.length - 1) {
                this.isUploading = false;
                this.selectedFiles = [];
                alert('Anexos enviados com sucesso!');
              }
            },
            error: (error) => {
              console.error('Erro ao criar anexo:', error);
              this.isUploading = false;
            }
          });
        },
        error: (error) => {
          console.error('Erro no upload:', error);
          this.isUploading = false;
        }
      });
    });
  }

  // ===== MÉTODO PARA CARREGAR ANEXOS EXISTENTES =====

  loadExistingAttachments(rentalId: number): void {
    
    this.vehicleRentalService.getVehicleRentalAttachments(rentalId).subscribe({
      next: (attachments) => {
        this.attachments = attachments;
      },
      error: (error) => {
        console.error('Erro ao carregar anexos existentes:', error);
        // Continuar mesmo sem anexos
      }
    });
  }

  // ===== MÉTODOS PARA GERENCIAR ANEXOS EXISTENTES =====

  downloadAttachment(attachment: any): void {
    
    if (!attachment.fileName) {
      alert('Nome do arquivo não encontrado');
      return;
    }

    // Usar o endpoint de download do Azure Blob Storage
    const downloadUrl = `${environment.microservices.rental}/blobrental/download?fileName=${encodeURIComponent(attachment.fileName)}`;
    
    
    // Criar link temporário para download
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = attachment.fileName;
    link.target = '_blank';
    
    // Adicionar ao DOM temporariamente e clicar
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  deleteAttachment(attachment: any, index: number): void {
    if (confirm(`Tem certeza que deseja excluir o anexo "${attachment.fileName}"?`)) {
      
      // Excluir do Azure Blob Storage
      this.deleteAttachmentFromBlob(attachment.fileName).then(() => {
        // Remover da lista local após exclusão bem-sucedida
        this.attachments.splice(index, 1);
      }).catch((error) => {
        console.error('Erro ao excluir anexo:', error);
        alert('Erro ao excluir anexo. Tente novamente.');
      });
    }
  }

  private async deleteAttachmentFromBlob(fileName: string): Promise<void> {
    try {
      const deleteUrl = `${environment.microservices.rental}/blobrental/delete/${encodeURIComponent(fileName)}`;
      
      const response = await fetch(deleteUrl, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Erro na exclusão: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
    } catch (error) {
      console.error('Erro ao excluir do blob storage:', error);
      throw error;
    }
  }

  // ===== MÉTODO PARA SALVAR ANEXOS NO UPDATE =====

  async saveAttachmentsToRental(rentalId: number): Promise<void> {
    if (this.attachments.length === 0) {
      return;
    }

    
    for (const attachment of this.attachments) {
      try {
        // Criar registro de anexo no backend
        const attachmentData = {
          vehicleRentalId: rentalId,
          fileName: attachment.fileName,
          filePath: attachment.filePath,
          fileSize: attachment.fileSize,
          mimeType: attachment.mimeType,
          category: attachment.category || 'DOCUMENTO',
          description: attachment.description || 'Anexo da locação',
          status: 'ATIVO',
          isRequired: false,
          isPublic: false
        };

        await this.vehicleRentalService.createVehicleRentalAttachment(attachmentData).toPromise();
      } catch (error) {
        console.error('Erro ao salvar anexo:', attachment.fileName, error);
        // Continuar com os outros anexos mesmo se um falhar
      }
    }
  }

  // Anexos serão gerenciados apenas na tela de edição/detalhes
}
